import React, { useRef, useState, useEffect } from 'react'
import clsx from 'clsx'
import { Image } from '../image/Image'
import { isTouchDevice as checkTouchDevice } from '@obeta/utils/lib/device-capabilities/isTouchDevice'
import { checkHybridDevice } from '@obeta/utils/lib/device-capabilities/checkHybridDevice'
import styles from './MagnifyImage.module.scss'

type MagnifyImageProps = {
  src: string
  alt?: string
  zoomScale?: number | number[]
  zoomMode?: 'hover' | 'click'
  onClick?: () => void
  className?: string
  onZoomIntrospect?: (zoomLevel: number | null) => void
}

type ZoomData = {
  isZoomed: boolean
  transform: string
  isHovering: boolean
  scale: number
  translateX: number
  translateY: number
}

export const MagnifyImage: React.FC<MagnifyImageProps> = ({
  src,
  alt = '',
  zoomScale = 2,
  zoomMode = 'hover',
  onClick,
  className,
  onZoomIntrospect,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isTouchDevice, setIsTouchDevice] = useState(false)

  // Devices that support both touch and mouse input simultaneously require a special approach, so we hide zoom icon for now
  const [isHybridDevice, setHybridDevice] = useState(false)

  useEffect(() => {
    setIsTouchDevice(checkTouchDevice())
    setHybridDevice(checkHybridDevice())
  }, [])

  // Convert zoomScale to array if it's a single number
  const zoomLevels = Array.isArray(zoomScale) ? zoomScale : [1, zoomScale]
  const [currentZoomIndex, setCurrentZoomIndex] = useState(0)
  // For hover mode, always use the higher zoom level; for click mode, use the current index
  const currentZoomLevel =
    zoomMode === 'hover'
      ? Array.isArray(zoomScale)
        ? Math.max(...zoomScale)
        : zoomScale
      : zoomLevels[currentZoomIndex]

  const [zoomData, setZoomData] = useState<ZoomData>({
    isZoomed: false,
    transform: 'translate(0px, 0px) scale(1)',
    isHovering: false,
    scale: 1,
    translateX: 0,
    translateY: 0,
  })

  // For pinch zoom gesture
  const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null)
  const [initialScale, setInitialScale] = useState<number>(1)
  const lastTouchRef = useRef<{ x: number; y: number } | null>(null)
  const [startTouch, setStartTouch] = useState<React.Touch | null>(null)

  const updateTransform = (
    scale: number,
    translateX: number,
    translateY: number,
    isZoomed = true
  ) => {
    setZoomData((prevData) => ({
      ...prevData,
      isZoomed,
      scale,
      translateX,
      translateY,
      transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
      isHovering: isZoomed,
    }))

    if (onZoomIntrospect) {
      onZoomIntrospect(isZoomed ? currentZoomIndex : null)
    }
  }

  const applyZoomTransform = (clientX: number, clientY: number, zoomLevel = currentZoomLevel) => {
    if (!containerRef.current) return
    // Only check for zoomLevel <= 1 in click mode
    if (zoomMode === 'click' && zoomLevel <= 1) return resetZoom()

    const rect = containerRef.current.getBoundingClientRect()
    const offsetX = clientX - rect.left
    const offsetY = clientY - rect.top

    const x = offsetX / rect.width
    const y = offsetY / rect.height

    const moveX = (x - 0.5) * (rect.width * (zoomLevel - 1))
    const moveY = (y - 0.5) * (rect.height * (zoomLevel - 1))

    updateTransform(zoomLevel, -moveX, -moveY)
  }

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    // Skip if this was triggered by a touch event (on hybrid devices)
    if ('touches' in e.nativeEvent) return

    setZoomData((prevData) => ({
      ...prevData,
      isHovering: true,
    }))
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // Skip mouse move handling if this was triggered by a touch event
    // This prevents slow panning due to both event handlers firing
    if ('touches' in e.nativeEvent) return

    if (!zoomData.isHovering) {
      setZoomData((prevData) => ({
        ...prevData,
        isHovering: true,
      }))
    }

    if (zoomMode === 'hover') {
      applyZoomTransform(e.clientX, e.clientY)
    } else if (zoomData.isZoomed) {
      // In click mode, still update position when zoomed
      applyZoomTransform(e.clientX, e.clientY)
    }
  }

  // No useEffect needed with this simpler approach

  const resetZoom = () => {
    setInitialPinchDistance(null)
    lastTouchRef.current = null

    updateTransform(1, 0, 0, false)

    if (zoomMode === 'click') {
      setCurrentZoomIndex(0)
    }
  }

  // Reset zoom when src changes
  useEffect(() => {
    resetZoom()
  }, [src]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    // Skip if this was triggered by a touch event
    if ('touches' in e.nativeEvent) return
    if (zoomMode === 'hover') {
      resetZoom()
    }
  }

  // Calculate distance between two touch points
  const getDistance = (touch1: React.Touch | Touch, touch2: React.Touch | Touch): number => {
    const dx = touch1.clientX - touch2.clientX
    const dy = touch1.clientY - touch2.clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  // Get center point between two touches
  const getMidpoint = (touch1: React.Touch | Touch, touch2: React.Touch | Touch) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    }
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    // Prevent double-tap zooming on iOS
    if (e.touches.length > 1) {
      e.preventDefault()
    }

    // Single touch for regular zoom
    if (e.touches.length === 1) {
      const touch = e.touches[0]
      setStartTouch(touch)
      lastTouchRef.current = { x: touch.clientX, y: touch.clientY }
    }

    // Pinch gesture detection
    if (e.touches.length === 2) {
      const distance = getDistance(e.touches[0], e.touches[1])
      setInitialPinchDistance(distance)
      setInitialScale(zoomData.scale)

      // Store the midpoint of the two touches
      const midpoint = getMidpoint(e.touches[0], e.touches[1])
      lastTouchRef.current = midpoint
    }
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    // Handle pinch zoom gesture
    if (e.touches.length === 2 && initialPinchDistance) {
      e.preventDefault() // Prevent default browser behavior

      const currentDistance = getDistance(e.touches[0], e.touches[1])
      const scaleFactor = currentDistance / initialPinchDistance

      // Apply scaling based on initial scale and pinch gesture
      const newScale = Math.max(
        1,
        Math.min(
          initialScale * scaleFactor,
          Array.isArray(zoomScale) ? Math.max(...zoomScale) * 1.5 : zoomScale * 1.5
        )
      )

      // Get the midpoint for panning
      const midpoint = getMidpoint(e.touches[0], e.touches[1])

      // Calculate pan adjustment if we have a last touch point
      let translateX = zoomData.translateX
      let translateY = zoomData.translateY

      if (lastTouchRef.current && newScale > 1) {
        // Calculate how much to adjust the pan based on touch movement
        const dx = midpoint.x - lastTouchRef.current.x
        const dy = midpoint.y - lastTouchRef.current.y

        // Adjust translation based on movement and scale
        translateX += dx
        translateY += dy

        // Update last touch point
        lastTouchRef.current = midpoint
      }

      // Apply the new transform
      updateTransform(newScale, translateX, translateY)
      return
    }

    // Handle single touch - standard zoom or pan
    if (e.touches.length === 1) {
      if (!startTouch) return
      const touch = e.touches[0]

      // If already zoomed in, allow panning
      if (zoomData.scale > 1 && lastTouchRef.current) {
        const dx = touch.clientX - lastTouchRef.current.x
        const dy = touch.clientY - lastTouchRef.current.y

        updateTransform(zoomData.scale, zoomData.translateX + dx, zoomData.translateY + dy)

        lastTouchRef.current = { x: touch.clientX, y: touch.clientY }
      } else {
        // Regular zoom behavior
        applyZoomTransform(touch.clientX, touch.clientY)
      }
    }
  }

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    // Just reset the pinch distance tracking, but maintain zoom level
    if (e.touches.length < 2) {
      setInitialPinchDistance(null)
    }

    // If we end with no touches and scale is very close to 1, just reset completely
    if (e.touches.length === 0 && zoomData.scale < 1.05) {
      resetZoom()
      return
    }

    // If we still have one finger down, update for panning
    if (e.touches.length === 1) {
      setStartTouch(e.touches[0])
      lastTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    }

    // Maintain zoom level for all other cases
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Skip if this was triggered by a touch event
    if ('touches' in e.nativeEvent) return
    // Don't use click-to-zoom on touch devices - prefer pinch gestures instead
    if (isTouchDevice && !isHybridDevice) {
      if (onClick) onClick()
      return
    }

    if (zoomMode === 'click') {
      e.stopPropagation() // Prevent onClick from firing if we're handling zoom

      // Toggle through zoom levels
      const nextZoomIndex = (currentZoomIndex + 1) % zoomLevels.length
      setCurrentZoomIndex(nextZoomIndex)

      if (nextZoomIndex === 0 || zoomLevels[nextZoomIndex] <= 1) {
        resetZoom()
      } else {
        applyZoomTransform(e.clientX, e.clientY, zoomLevels[nextZoomIndex])
      }
    }
  }

  // Add passive: false to allow preventDefault() on touch events
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const options = { passive: false }

    const touchStartHandler = (e: TouchEvent) => {
      e.preventDefault() //prevent default double tabs, zooming or scrolling
      // Mark as touch device on first touch
      if (!isTouchDevice) {
        setIsTouchDevice(true)
      }
    }

    const touchMoveHandler = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        e.preventDefault()
      }
    }

    container.addEventListener('touchstart', touchStartHandler, options)
    container.addEventListener('touchmove', touchMoveHandler, options)

    return () => {
      container.removeEventListener('touchstart', touchStartHandler)
      container.removeEventListener('touchmove', touchMoveHandler)
    }
  }, [isTouchDevice])

  // Determine the effective zoom mode - never use click mode on touch devices
  const effectiveZoomMode = isTouchDevice && !isHybridDevice ? 'hover' : zoomMode

  return (
    <div
      className={clsx(styles.zoomContainerImg, className, {
        [styles.hovering]: zoomData.isHovering && effectiveZoomMode !== 'click',
        [styles.noZoonIn]: isTouchDevice && !isHybridDevice,
      })}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={effectiveZoomMode === 'click' ? handleClick : onClick}
    >
      <Image
        variant="product"
        src={src}
        alt={alt}
        className={styles.zoomImage}
        style={{
          transform: zoomData.transform,
          transition: zoomData.isZoomed ? 'none' : 'transform 0.3s ease-out',
        }}
        onClick={onClick}
      />
    </div>
  )
}
